<template>
  <div class="home-page">
    <div class="home-page__hero">
      <h1 class="home-page__hero__text" v-sanitize-html="$t('homepage.hero.text')"></h1>
      <SButton class="home-page__hero__cta" :link="ANGEBOT_PATH"> {{ $t('homepage.hero.cta') }}</SButton>
    </div>

    <NavigationGrid />
    <MotivesGrid
      :title="$t('homepage.popular_themes.title')"
      :motives="populareMotives"
      src-source="/assets/artnight/homepage/popular-themes-grid/"
      :button-link="MOTIV_PATH"
      translation-path="homepage.popular_themes.motiv"
      :button-text="$t('homepage.popular_themes.cta')"
    />
    <ArtnightUSPs />
    <AimieMessage />
    <MotivesGrid
      :title="$t('homepage.new_themes.title')"
      s
      :motives="newMotives"
      src-source="/assets/artnight/homepage/new-themes-grid/"
      :button-link="{
        path: MOTIV_PATH,
        query: {
          orderBy: 'key',
          order: 'DESC'
        }
      }"
      translation-path="homepage.new_themes.motiv"
      :button-text="$t('homepage.new_themes.cta')"
    />
    <Cities />
    <GroupEvents />
    <MotivesGrid
      :title="$t('homepage.online_events.title')"
      :motives="onlineMotives"
      src-source="/assets/artnight/homepage/online-events-grid/"
      :button-link="ONLINE_PRODUCTS_PATH"
      translation-path="homepage.online_events.motiv"
      :button-text="$t('homepage.online_events.cta')"
    />
    <HowItWorks />

    <EventsCategory />
  </div>
</template>

<script setup lang="ts">
import { ANGEBOT_PATH, MOTIV_PATH, ONLINE_PRODUCTS_PATH } from '@/resources/helpers';
import { useSeoStore } from '@/stores/seo';
import { storeToRefs } from 'pinia';

const NuxtTransformImage = defineAsyncComponent(() => import('@/components/molecules/NuxtTransformImage.vue'));
const SButton = defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue'));
const MotivesGrid = defineAsyncComponent(() => import('@/components/molecules/MotivesGrid.vue'));
const NavigationGrid = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/NavigationGrid.vue'));
const ArtnightUSPs = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/ArtnightUSPs.vue'));
const AimieMessage = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/AimieMessage.vue'));
const Cities = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/Cities.vue'));
const GroupEvents = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/GroupEvents.vue'));
const HowItWorks = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/HowItWorks.vue'));
const EventsCategory = defineAsyncComponent(() => import('@/components/Pages/Homepage/children/EventsCategory.vue'));

const config = useAppConfig();

const populareMotives = [
  { name: 'structure', link: '/motiv/struktur-abstrakt-malen-1041567' },
  { name: 'banksy', link: '/motiv/banksy-street-art-kollektion-malen-2246435' },
  { name: 'inspiration', link: '/motiv/abstrakt-inspiration-malen-2929487' },
  { name: 'spatula', link: '/motiv/abstraktes-arbeiten-mit-dem-spachtel-malen-1414672' },
  { name: 'glitter', link: '/motiv/glitzer-abstrakt-malen-257659' },
  { name: 'partner', link: '/motiv/paint-your-partner-malen-2868099' },
  { name: 'kids_eule', link: '/motiv/artnight-kids-eule-malen-2784873' },
  { name: 'city_passenger', link: '/motiv/stadtspaziergang-malen-2296512' },
  { name: 'eule', link: '/motiv/eule-malen-2865556' },
  { name: 'layering', link: '/motiv/abstract-layering-malen-991289' },
  { name: 'monet', link: '/motiv/artnight-klassik-monet-seerosen-malen-257746' },
  { name: 'dancing', link: '/motiv/tanzerin-im-licht-malen-257566' },
  { name: 'frida_acryl_collage', link: '/motiv/artnight-klassik-frida-kahlo-acryl-collage-malen-257524' },
  { name: 'zuckerwatte_wolken', link: '/motiv/zuckerwattewolken-malen-2421121' },
  { name: 'goldener_see', link: '/motiv/goldener-see-malen-2361770' },
  { name: 'lyon', link: '/motiv/artnight-lowe-malen-257577' }
];

const newMotives = [
  { name: 'lemons', link: '/motiv/paint-and-frame-zitronen-malen-3049351' },
  { name: 'oranges', link: '/motiv/paint-and-frame-orangen-malen-3049344' },
  { name: 'oysters', link: '/motiv/the-art-of-oyster-painting-and-decoupage-malen-3045986' },
  { name: 'winenpaint', link: '/motiv/wine-and-paint-struktur-abstrakt-malen-3044778' },
  { name: 'kids', link: '/motiv/artnight-kids-aquarell-kleine-monsterwerkstatt-malen-3039220' },
  { name: 'frog', link: '/motiv/artnight-kids-herr-frosch-malen-3021430' },
  { name: 'beat', link: '/motiv/artnight-beats-malen-3004716' },
  { name: 'afro_beats', link: '/motiv/artnight-beats-afro-beats-malen-2992338' },
  { name: 'woman', link: '/motiv/unter-der-sonne-des-sudens-malen-2990552' },
  { name: 'fruit', link: '/motiv/pixel-painting-discofruchte-malen-2977883' },
  { name: 'disco', link: '/motiv/pixel-painting-discokugel-malen-2976866' },
  { name: 'sunrise', link: '/motiv/tequila-sunrise-malen-2961505' }
];

const onlineMotives = [
  { name: 'vom_winde_verweht', themeUuid: 'bceaa0ab-aef7-5bc3-b982-faf7d27df9e0', isAsyncLink: true },
  { name: 'poney', themeUuid: '0562e161-bdc4-526e-9182-640cec88020c', isAsyncLink: true },
  { name: 'lemon_tree', themeUuid: '0718be49-228c-594f-a994-12937ac3057f', isAsyncLink: true },
  { name: 'la_dolce_vita', themeUuid: '4db9d3a4-eb14-5dee-a535-9aed7fd103df', isAsyncLink: true },
  { name: 'lavender', themeUuid: 'cbeebc57-52d8-586d-a5c9-6bbe91a7d8a2', isAsyncLink: true },
  { name: 'wattwanderung', themeUuid: '33e9944a-c3f4-501d-a994-5a6b987a483e', isAsyncLink: true },
  { name: 'sternennacht', themeUuid: 'a3255a0b-0ca6-51ef-9158-415ef415f3e4', isAsyncLink: true },
  { name: 'sommerwind', themeUuid: 'c34ca76a-3e85-5321-aae3-595d69a8306d', isAsyncLink: true }
];

const seoStore = useSeoStore();
const { seoImageUrl } = storeToRefs(seoStore);

watchEffect(() => {
  useSeoMeta({
    title: config.seo.headTitle,
    description: config.seo.headDescription,
    ogTitle: config.seo.headTitle,
    ogDescription: config.seo.headDescription,
    ogImage: seoImageUrl.value,
    twitterTitle: config.seo.headTitle,
    twitterDescription: config.seo.headDescription,
    twitterImage: '[twitter:image]',
    twitterCard: 'summary'
  });
});
</script>

<style lang="scss" scoped>
@use '@/assets/styles/css_BASE/helpers/mixins/font-size.scss' as *;
@use '@/assets/styles/css_BASE/helpers/mixins/_bp.scss' as *;

.home-page {
  color: var(--color-night-blue-900);
  &__hero {
    height: 390px;
    background: url('/assets/artnight/bgs/hero-mobile.png') no-repeat center;
    background-size: cover;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end; // Centers content vertically
    text-align: center;
    padding-bottom: 25px;
    gap: 80px;
    @include bp(sm-up) {
      background: url('/assets/artnight/bgs/hero-desktop.png') no-repeat center;
      padding-bottom: 20px;
      gap: 84px;
    }
    &__text {
      width: 300px;
      color: var(--color-white);
      @include font-size(48px);
      font-weight: 700;
      line-height: 48px;
      letter-spacing: 2px;
      @include bp(sm-up) {
        width: 600px;
        @include font-size(60px);
        line-height: 130%;
        letter-spacing: -0.5px;
      }
    }
  }
}
</style>
